import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { api } from '../lib/api';
import { useAuth } from '../contexts/auth';
import { FamilyRole, GuardianRelationship } from '@kinapp/shared';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Dependent {
  id: string;
  name: string;
  dateOfBirth?: string;
  notes?: string;
  isActive: boolean;
  guardianRelationships?: GuardianRelationship[];
  emergencyProfile?: {
    gpName?: string; gpPhone?: string; nhsNumber?: string;
    conditions?: string; allergies?: string; bloodType?: string;
    hospitalPreference?: string; nextOfKinName?: string; nextOfKinPhone?: string;
  };
}

interface Member {
  id: string;
  role: FamilyRole;
  user: { id: string; name: string; email: string };
}

// ─── DependentSettingsPage ────────────────────────────────────────────────────

export function DependentSettingsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [familyId, setFamilyId] = useState('');
  const [dep, setDep] = useState<Dependent | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [notes, setNotes] = useState('');
  const [gpName, setGpName] = useState('');
  const [gpPhone, setGpPhone] = useState('');
  const [nhsNumber, setNhsNumber] = useState('');
  const [conditions, setConditions] = useState('');
  const [allergies, setAllergies] = useState('');
  const [bloodType, setBloodType] = useState('');
  const [hospitalPreference, setHospitalPreference] = useState('');
  const [nextOfKinName, setNextOfKinName] = useState('');
  const [nextOfKinPhone, setNextOfKinPhone] = useState('');
  const [relationships, setRelationships] = useState<Record<string, string>>({});

  // Emergency token state
  const [emergencyTokens, setEmergencyTokens] = useState<Array<{ id: string; token: string; label?: string; createdAt: string }>>([]);
  const [creatingToken, setCreatingToken] = useState(false);
  const [revokingTokenId, setRevokingTokenId] = useState<string | null>(null);
  const [copiedTokenId, setCopiedTokenId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const families = await api.getFamilies();
        if (!families.length) { navigate('/onboarding'); return; }
        const fid = families[0].id;
        setFamilyId(fid);
        const [deps, mems] = await Promise.all([
          api.getDependents(fid, true),
          api.getFamilyMembers(fid),
        ]);
        const found = (deps as Dependent[]).find((d) => d.id === id);
        if (!found) { navigate('/settings'); return; }

        setDep(found);
        setMembers(mems as Member[]);
        setName(found.name);
        setDob(found.dateOfBirth ? found.dateOfBirth.split('T')[0] : '');
        setNotes(found.notes ?? '');
        setGpName(found.emergencyProfile?.gpName ?? '');
        setGpPhone(found.emergencyProfile?.gpPhone ?? '');
        setNhsNumber(found.emergencyProfile?.nhsNumber ?? '');
        setConditions(found.emergencyProfile?.conditions ?? '');
        setAllergies(found.emergencyProfile?.allergies ?? '');
        setBloodType(found.emergencyProfile?.bloodType ?? '');
        setHospitalPreference(found.emergencyProfile?.hospitalPreference ?? '');
        setNextOfKinName(found.emergencyProfile?.nextOfKinName ?? '');
        setNextOfKinPhone(found.emergencyProfile?.nextOfKinPhone ?? '');

        // Load emergency tokens
        try {
          const tokens = await api.getEmergencyTokens(found.id);
          setEmergencyTokens(tokens as any[]);
        } catch { /* ignore */ }

        const relMap: Record<string, string> = {};
        for (const gr of found.guardianRelationships ?? []) {
          relMap[gr.userId] = gr.relationship;
        }
        setRelationships(relMap);

        const me = (mems as Member[]).find((m) => m.user.id === user?.id);
        setIsOwner(me?.role === 'OWNER');
      } catch {
        setError('Unable to load. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate, user?.id]);

  const cardUrl = (token: string) => `${window.location.origin}/card/${token}`;

  const handleCreateToken = async () => {
    if (!dep) return;
    setCreatingToken(true);
    try {
      const t = await api.createEmergencyToken(dep.id) as { id: string; token: string };
      setEmergencyTokens((prev) => [{ id: t.id, token: t.token, createdAt: new Date().toISOString() }, ...prev]);
    } catch {
      setError('Failed to create emergency card link. Please try again.');
    } finally {
      setCreatingToken(false);
    }
  };

  const handleRevokeToken = async (tokenId: string) => {
    setRevokingTokenId(tokenId);
    try {
      await api.deleteEmergencyToken(tokenId);
      setEmergencyTokens((prev) => prev.filter((t) => t.id !== tokenId));
    } catch {
      setError('Failed to revoke link. Please try again.');
    } finally {
      setRevokingTokenId(null);
    }
  };

  const handleCopyUrl = async (token: string) => {
    await navigator.clipboard.writeText(cardUrl(token));
    setCopiedTokenId(token);
    setTimeout(() => setCopiedTokenId(null), 2000);
  };

  const handleSave = async () => {
    if (!name.trim() || !familyId || !dep) return;
    setSaving(true);
    setError('');
    try {
      const guardianRelationships = members
        .filter((m) => relationships[m.user.id]?.trim())
        .map((m) => ({ userId: m.user.id, relationship: relationships[m.user.id].trim() }));

      await api.updateDependent(familyId, dep.id, {
        name: name.trim(),
        dateOfBirth: dob || null,
        notes: notes.trim() || null,
        guardianRelationships,
      });
      await api.updateEmergencyProfile(dep.id, {
        gpName: gpName.trim() || null,
        gpPhone: gpPhone.trim() || null,
        nhsNumber: nhsNumber.trim() || null,
        conditions: conditions.trim() || null,
        allergies: allergies.trim() || null,
        bloodType: bloodType.trim() || null,
        hospitalPreference: hospitalPreference.trim() || null,
        nextOfKinName: nextOfKinName.trim() || null,
        nextOfKinPhone: nextOfKinPhone.trim() || null,
      });
      navigate('/settings');
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!dep || !familyId) return;
    if (!confirm(`Archive ${dep.name}? They will be hidden from the dashboard but their records will be kept.`)) return;
    setArchiving(true);
    try {
      await api.updateDependent(familyId, dep.id, { isActive: false });
      navigate('/settings');
    } catch (err: any) {
      setError(err.message || 'Failed to archive');
      setArchiving(false);
    }
  };

  const handleRestore = async () => {
    if (!dep || !familyId) return;
    setArchiving(true);
    try {
      await api.updateDependent(familyId, dep.id, { isActive: true });
      navigate('/settings');
    } catch (err: any) {
      setError(err.message || 'Failed to restore');
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        Loading…
      </div>
    );
  }

  if (!dep) return null;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back */}
      <button
        onClick={() => navigate('/settings')}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 mb-6"
      >
        ← Settings
      </button>

      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-xl font-semibold text-gray-900">{dep.name}</h1>
        {!dep.isActive && (
          <span className="text-xs font-normal text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
            Archived
          </span>
        )}
      </div>

      {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

      <div className="space-y-4">
        {/* Basic info */}
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-4">Basic info</h2>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} disabled={!isOwner} className="input w-full" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Date of birth</label>
              <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} disabled={!isOwner} className="input w-full" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Notes</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!isOwner} rows={2} className="input w-full resize-none" />
            </div>
          </div>
        </section>

        {/* Doctor / GP */}
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-4">Doctor / GP</h2>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">GP name</label>
              <input value={gpName} onChange={(e) => setGpName(e.target.value)} disabled={!isOwner} placeholder="Dr. Smith" className="input w-full" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">GP phone</label>
              <input value={gpPhone} onChange={(e) => setGpPhone(e.target.value)} disabled={!isOwner} placeholder="020 1234 5678" className="input w-full" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">NHS / insurance number</label>
              <input value={nhsNumber} onChange={(e) => setNhsNumber(e.target.value)} disabled={!isOwner} placeholder="ABC 123 4567" className="input w-full" />
            </div>
          </div>
        </section>

        {/* Medical */}
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-4">Medical</h2>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Conditions</label>
              <textarea value={conditions} onChange={(e) => setConditions(e.target.value)} disabled={!isOwner} rows={2} placeholder="e.g. Type 2 diabetes, hypertension" className="input w-full resize-none" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Allergies</label>
              <textarea value={allergies} onChange={(e) => setAllergies(e.target.value)} disabled={!isOwner} rows={2} placeholder="e.g. Penicillin, latex" className="input w-full resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Blood type</label>
                <input value={bloodType} onChange={(e) => setBloodType(e.target.value)} disabled={!isOwner} placeholder="e.g. A+" className="input w-full" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Hospital preference</label>
                <input value={hospitalPreference} onChange={(e) => setHospitalPreference(e.target.value)} disabled={!isOwner} placeholder="e.g. St Thomas'" className="input w-full" />
              </div>
            </div>
          </div>
        </section>

        {/* Next of kin */}
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-4">Next of kin</h2>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Name</label>
              <input value={nextOfKinName} onChange={(e) => setNextOfKinName(e.target.value)} disabled={!isOwner} placeholder="Jane Smith" className="input w-full" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Phone</label>
              <input value={nextOfKinPhone} onChange={(e) => setNextOfKinPhone(e.target.value)} disabled={!isOwner} placeholder="07700 123456" className="input w-full" />
            </div>
          </div>
        </section>

        {/* Emergency card */}
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-start justify-between mb-1">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Emergency card</h2>
          </div>
          <p className="text-xs text-gray-400 mb-4">
            A public link (no login required) that paramedics and A&amp;E staff can open to see critical information and current medications.
          </p>
          {emergencyTokens.length === 0 ? (
            <button
              onClick={handleCreateToken}
              disabled={creatingToken}
              className="w-full py-2.5 rounded-xl border border-dashed border-gray-300 text-sm text-gray-500 hover:text-primary-700 hover:border-primary-300 hover:bg-primary-50/30 transition-colors disabled:opacity-50"
            >
              {creatingToken ? 'Creating…' : '+ Create emergency card link'}
            </button>
          ) : (
            <div className="space-y-4">
              {emergencyTokens.map((t) => (
                <div key={t.id} className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-3">
                  {/* QR code */}
                  <div className="flex justify-center py-2">
                    <QRCode value={cardUrl(t.token)} size={140} />
                  </div>
                  {/* URL + copy */}
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={cardUrl(t.token)}
                      className="flex-1 text-xs bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600 font-mono truncate"
                    />
                    <button
                      onClick={() => handleCopyUrl(t.token)}
                      className="shrink-0 text-xs px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                    >
                      {copiedTokenId === t.token ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  {/* Revoke */}
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-xs text-gray-400">Created {new Date(t.createdAt).toLocaleDateString()}</span>
                    <button
                      onClick={() => handleRevokeToken(t.id)}
                      disabled={revokingTokenId === t.id}
                      className="text-xs text-red-400 hover:text-red-600 disabled:opacity-50 transition-colors"
                    >
                      {revokingTokenId === t.id ? 'Revoking…' : 'Revoke'}
                    </button>
                  </div>
                </div>
              ))}
              <button
                onClick={handleCreateToken}
                disabled={creatingToken}
                className="w-full py-2 rounded-xl border border-dashed border-gray-300 text-xs text-gray-400 hover:text-primary-700 hover:border-primary-300 transition-colors disabled:opacity-50"
              >
                {creatingToken ? 'Creating…' : '+ Create another link'}
              </button>
            </div>
          )}
        </section>

        {/* GP summary */}
        <section className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">GP summary</h2>
          <p className="text-xs text-gray-400 mb-4">
            Print a structured summary of medications, appointments, and care notes ready for a GP appointment.
          </p>
          <button
            onClick={() => dep && navigate(`/gp-summary/${dep.id}`)}
            className="w-full py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-700 font-medium hover:bg-gray-100 transition-colors"
          >
            Open GP summary →
          </button>
        </section>

        {/* Relationships */}
        {members.length > 0 && (
          <section className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Relationships</h2>
            <p className="text-xs text-gray-400 mb-4">How is each family member related to {dep.name}?</p>
            <div className="space-y-2">
              {members.map((m) => (
                <div key={m.user.id} className="flex items-center gap-3">
                  <span className="text-sm text-gray-700 w-28 shrink-0 truncate">{m.user.name}</span>
                  <input
                    type="text"
                    value={relationships[m.user.id] ?? ''}
                    onChange={(e) => setRelationships((prev) => ({ ...prev, [m.user.id]: e.target.value }))}
                    disabled={!isOwner}
                    placeholder="e.g. Son, Daughter, Carer"
                    className="input flex-1 py-1.5 text-sm"
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Actions */}
        {isOwner && (
          <div className="flex flex-col gap-3">
            <button onClick={handleSave} disabled={saving || !name.trim()} className="btn btn-primary w-full">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            {dep.isActive ? (
              <button
                onClick={handleArchive}
                disabled={archiving}
                className="text-sm text-amber-600 hover:text-amber-800 hover:underline text-center py-1"
              >
                {archiving ? 'Archiving…' : 'Archive dependent'}
              </button>
            ) : (
              <button
                onClick={handleRestore}
                disabled={archiving}
                className="text-sm text-green-600 hover:text-green-800 hover:underline text-center py-1"
              >
                {archiving ? 'Restoring…' : 'Restore to active'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
